import React from 'react'
import Navbar from '@/layout/navbar/Navbar'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import Typography from '@mui/material/Typography'
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar'
import Breadcrumbs from '@mui/material/Breadcrumbs'
import Skeleton from '@mui/material/Skeleton'
import Link from '@mui/material/Link'
import LinkedInButton from '@/components/buttons/LinkedInButton'
import { getWindowDimensions } from '@/utils/screenUtils'
import { Outlet, useLocation, useParams, useNavigate } from 'react-router-dom'
import { useLazyGetSchemaQuery } from '@/store/apis/schemas'
import { useTranslation } from 'react-i18next'

const drawerWidth = 240

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: drawerWidth,
  }),
}))

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean
  marginleft: number
}>(({ theme, open }) => ({
  maxWidth: '100vw',
  overflowX: 'scroll',
  flexGrow: 1,
  padding: '30px',
  paddingBottom: 0,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: 'relative',
}))

const BaseLayout: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const urlParams = useParams()
  const navigate = useNavigate()
  const [getSchemaQuery, { data: schemaData, isFetching }] =
    useLazyGetSchemaQuery()
  const names = location.pathname.split('/')

  React.useEffect(() => {
    if (urlParams?.schemaId) {
      getSchemaQuery({ id: Number(urlParams.schemaId) })
    }
  }, [getSchemaQuery, urlParams])

  const [windowDimensions, setWindowDimensions] = React.useState(
    getWindowDimensions(),
  )
  React.useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  const isMobileWidth = windowDimensions.width < 900
  const [isDrawerOpen, setDrawerOpen] = React.useState<boolean>(!isMobileWidth)

  const schemaName =
    !isFetching && schemaData ? (
      schemaData?.schema.schema.name
    ) : (
      <Skeleton variant="text" width={100} sx={{ marginLeft: '5px' }} />
    )
  const schemaIds = JSON.parse(localStorage.getItem('schemaIds'))
  let shouldRenderBreadcrumb = true

  const paths: {
    name: string | React.ReactNode
    href?: string
    isLoading: boolean
  }[] = []
  switch (names[1]) {
    case 'schema':
      shouldRenderBreadcrumb = schemaIds.length < 2
      paths.push({
        name: t('baseLayout.schema'),
        href: `/${names[1]}`,
        isLoading: false,
      })
      if (names.length > 2) {
        paths.push({
          name: schemaName,
          isLoading: !(!isFetching && schemaData),
        })
      }
      if (names.length > 3) {
        switch (names[3]) {
          case 'schema':
            paths.push({
              name: t('baseLayout.schema'),
              isLoading: false,
            })
            break
          case 'created':
            paths.push({
              name: t('baseLayout.createdSchemas'),
              isLoading: false,
            })
            break
          case 'files':
            paths.push({
              name: t('baseLayout.files'),
              isLoading: false,
            })
            break
        }
      }
      break
    case 'users':
      paths.push({
        name: t('baseLayout.users'),
        href: names[1],
        isLoading: false,
      })
      break
    case 'schemas':
      paths.push({
        name: t('baseLayout.schemas'),
        href: `/${names[1]}`,
        isLoading: false,
      })
      if (names.length > 2) {
        paths.push({
          name: (
            <>
              <span style={{ marginLeft: '5px' }}>{schemaName}</span>
            </>
          ),
          isLoading: !(!isFetching && schemaData),
        })
      }
      if (names.length > 3) {
        switch (names[3]) {
          case 'attendance':
            paths.push({
              name: t('baseLayout.attendance'),
              isLoading: false,
            })
            break
          case 'users':
            paths.push({
              name: t('baseLayout.users'),
              isLoading: false,
            })
            break
          case 'create':
            paths.push({
              name: t('baseLayout.editSchema'),
              isLoading: false,
            })
            break
          case 'scenes':
            paths.push({
              name: t('baseLayout.scenes'),
              isLoading: false,
            })
            break
          case 'scene-groups':
            paths.push({
              name: t('baseLayout.sceneGroups'),
              isLoading: false,
            })
            break
          case 'places':
            paths.push({
              name: t('baseLayout.places'),
              href: `${names[1]}/${names[2]}/${names[3]}`,
              isLoading: false,
            })
            break
        }
      }
      if (names.length > 4) {
        const sceneId = urlParams ? Number(urlParams.sceneId) : undefined
        const scen = sceneId
          ? schemaData?.scenes.find((scen) => scen.scene.id === sceneId)
          : undefined
        const sceneName = scen ? (
          scen.scene.name
        ) : (
          <Skeleton variant="text" width={100} sx={{ marginLeft: '5px' }} />
        )
        paths.push({
          name: (
            <>
              {t('baseLayout.scene')}:
              <span style={{ marginLeft: '5px' }}>{sceneName}</span>
            </>
          ),
          href: `${names[1]}/${names[2]}/${names[3]}/${names[4]}`,
          isLoading: !(!isFetching && schemaData),
        })
      }
      break
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar position="fixed" open={isDrawerOpen}>
        <Toolbar
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            backgroundColor: '#353839',
          }}
        >
          <IconButton
            color="inherit"
            onClick={() => setDrawerOpen(true)}
            edge="start"
            sx={{
              marginRight: 5,
              ...(isDrawerOpen && { display: 'none' }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {paths[0]?.name}
          </Typography>
          {((isMobileWidth && !isDrawerOpen) || !isMobileWidth) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
              }}
            >
              <LinkedInButton />
            </div>
          )}
        </Toolbar>
      </AppBar>
      <Navbar
        drawerWidth={drawerWidth}
        isOpen={isDrawerOpen}
        toggleDrawerOpen={() => setDrawerOpen(false)}
        isMobileWidth={isMobileWidth}
      />
      <Main open={isDrawerOpen} marginleft={isMobileWidth ? 0 : -drawerWidth}>
        <Toolbar />
        {shouldRenderBreadcrumb && paths.length > 1 && (
          <Breadcrumbs sx={{ marginBottom: '2em' }}>
            {paths.map((path, index) => (
              <div key={index} style={{ display: 'flex' }}>
                {!path.isLoading && path.href ? (
                  <Link
                    underline="hover"
                    color="inherit"
                    onClick={() => navigate(path.href)}
                    style={{ cursor: 'pointer' }}
                  >
                    {path.name}
                  </Link>
                ) : (
                  <>{path.name}</>
                )}
              </div>
            ))}
          </Breadcrumbs>
        )}
        <Outlet context={{ isDrawerOpen }} />
      </Main>
    </Box>
  )
}

export default BaseLayout
